import { z } from 'zod';
import { exerciseVersionSchema, exerciseVideoSchema, optionalInt, optionalText, } from './exercises.js';
/** D-033: maximaal aantal oefeningen in één training. */
export const MAX_TRAINING_ITEMS = 40;
// --- Invoer -------------------------------------------------------------------
export const trainingItemInputSchema = z.object({
    /** Bestaande regel: houdt zijn vastgezette versie. Zonder id: nieuwe regel. */
    id: z.uuid().optional(),
    exerciseId: z.uuid({ message: 'Ongeldige oefening.' }),
    plannedDurationMinutes: optionalInt(1, 180, 'Duur'),
    trainerNotes: optionalText(1000),
});
/** POST en PUT: volledige training, regels op volgorde. */
export const trainingInputSchema = z.object({
    title: z
        .string()
        .trim()
        .min(3, { message: 'Geef de training een titel van minimaal 3 tekens.' })
        .max(160, { message: 'Gebruik maximaal 160 tekens.' }),
    description: optionalText(2000),
    scheduledAt: z.iso
        .datetime({ offset: true, message: 'Ongeldige datum of tijd.' })
        .nullable()
        .optional(),
    teamName: optionalText(80),
    playerCount: optionalInt(1, 60, 'Aantal spelers'),
    courtCount: optionalInt(1, 10, 'Aantal velden'),
    targetDurationMinutes: optionalInt(5, 480, 'Gewenste duur'),
    /** D-037: lezen en dupliceren door clubgenoten; alleen bij een training in een club. */
    shareWithClub: z.boolean().default(false),
    items: z
        .array(trainingItemInputSchema)
        .max(MAX_TRAINING_ITEMS, { message: `Gebruik maximaal ${MAX_TRAINING_ITEMS} oefeningen.` })
        .refine((items) => {
        const ids = items.flatMap((i) => (i.id ? [i.id] : []));
        return new Set(ids).size === ids.length;
    }, 'Een regel komt dubbel voor.')
        .default([]),
});
/** mine = eigen trainingen; club = gedeelde trainingen van anderen in je actieve club (D-037). */
export const trainingScopeSchema = z.enum(['mine', 'club']);
export const trainingListQuerySchema = z.object({
    scope: trainingScopeSchema.default('mine'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
// --- Uitvoer ------------------------------------------------------------------
/** Som van de bekende duren; regels zonder duur tellen nooit als 0 (docs/03). */
export const trainingTotalsSchema = z.object({
    knownMinutes: z.number().int(),
    unknownCount: z.number().int(),
});
export const trainingCardSchema = z.object({
    id: z.string(),
    title: z.string(),
    scheduledAt: z.string().nullable(),
    teamName: z.string().nullable(),
    targetDurationMinutes: z.number().int().nullable(),
    itemCount: z.number().int(),
    totals: trainingTotalsSchema,
    authorName: z.string(),
    isMine: z.boolean(),
    shared: z.boolean(),
    updatedAt: z.string(),
});
export const trainingListResponseSchema = z.object({
    trainings: z.array(trainingCardSchema),
    nextCursor: z.string().nullable(),
});
export const trainingItemSchema = z.object({
    id: z.string(),
    position: z.number().int(),
    /** Alleen het ID (geen inhoud), zodat PUT de regel kan behouden ook zonder toegang. */
    exerciseId: z.string(),
    plannedDurationMinutes: z.number().int().nullable(),
    trainerNotes: z.string().nullable(),
    /** Geplande duur, anders de duur uit de vastgezette versie; null = onbekend. */
    effectiveDurationMinutes: z.number().int().nullable(),
    /** null: de maker mag deze oefening niet meer zien (D-033). */
    exercise: z
        .object({
        id: z.string(),
        version: exerciseVersionSchema,
        /** Er is een nieuwere versie die de maker mag zien; bijwerken is een bewuste actie. */
        newerVersionNumber: z.number().int().nullable(),
        video: exerciseVideoSchema.nullable(),
    })
        .nullable(),
});
export const trainingDetailSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    scheduledAt: z.string().nullable(),
    teamName: z.string().nullable(),
    playerCount: z.number().int().nullable(),
    courtCount: z.number().int().nullable(),
    targetDurationMinutes: z.number().int().nullable(),
    organizationName: z.string().nullable(),
    /** D-037: alleen de maker bewerkt; clubgenoten lezen en dupliceren. */
    isMine: z.boolean(),
    authorName: z.string(),
    shared: z.boolean(),
    /** Delen kan alleen bij een training die in een club is gemaakt. */
    canShare: z.boolean(),
    items: z.array(trainingItemSchema),
    totals: trainingTotalsSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const trainingDetailResponseSchema = z.object({ training: trainingDetailSchema });
//# sourceMappingURL=trainings.js.map